// Ficha 01b · Teléfono, correo y términos: código del teléfono, correo verificado y los 2 consentimientos.
// Continuar exige el teléfono verificado, el correo verificado y las 2 casillas.
import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from '@tanstack/react-router';
import { toast } from 'sonner';
import { ArrowRight } from 'lucide-react';
import { Button, Loading } from '@/kit';
import { ApiError } from '@/api/errors';
import { useAcceptConsents, useResendOtp, useVerifyOtp, useWizardSession } from '../api/useWizard';
import { wizardErrorMsg } from '../api/wizardError';
import { WizardCard, WizardCardBody, WizardCardHead } from '../components/WizardCard';
import { WizardFootNav } from '../components/WizardFootNav';
import { EmailVerifyBlock } from '../components/EmailVerifyBlock';
import { ConsentCheck } from '../components/ConsentCheck';
import { emptyOtpCode, OtpCodeBoxes } from '../components/OtpCodeBoxes';
import { EMAIL_NOT_VERIFIED_MESSAGE, isEmailAlreadyExists } from '../emailOtpMessages';
import { emailSettled } from '../sessionEmail';
import { formatPhone } from '../phoneFormat';
import { otpSettled, resumeReasonText, stepAfterOtp } from '../resume';
import {
    ACCOUNT_OPENING_CONSENTS_ACCEPTED,
    ACCOUNT_OPENING_OTP_EYEBROW,
    ACCOUNT_OPENING_OTP_SUB,
    ACCOUNT_OPENING_RESUMED,
    ACCOUNT_OPENING_RESUMED_SEND_FAILED,
    hasAccountOpeningMark,
} from '../accountOpening';
import { sendOtpMessage, verifyOtpMessage } from '../otpMessages';
import {
    formatCountdown,
    OTP_LIFETIME_TEXT,
    OTP_RESEND_SECONDS,
    otpSentMessage,
    resendWaitSeconds,
} from '../otpResend';
import type { TakenEmail } from '../types';

/** Qué falta para poder continuar, en una línea. */
function consentHint(consentsAccepted: boolean, accountOpening: boolean): string {
    if (consentsAccepted && accountOpening) return ACCOUNT_OPENING_CONSENTS_ACCEPTED;
    if (consentsAccepted) return 'El cliente ya aceptó los términos en esta venta.';
    return 'Verifica el teléfono y el correo, y marca las dos casillas para continuar.';
}

/** Aviso de la venta o apertura retomada; si el envío falló, no promete que el código ya salió. */
function resumedText(sendFailed: boolean, accountOpening: boolean): string {
    if (accountOpening) {
        return sendFailed ? ACCOUNT_OPENING_RESUMED_SEND_FAILED : ACCOUNT_OPENING_RESUMED;
    }
    return sendFailed
        ? 'Venta retomada: el teléfono se confirma con un código nuevo.'
        : 'Venta retomada: le mandamos un código nuevo.';
}

export function OtpStep({ sessionId }: { sessionId: string }) {
    const navigate = useNavigate();
    const { data: session, refresh } = useWizardSession(sessionId);
    const verifyOtp = useVerifyOtp(sessionId);
    const resendOtp = useResendOtp(sessionId);
    const acceptConsents = useAcceptConsents(sessionId);

    const [code, setCode] = useState<string[]>(emptyOtpCode);
    const [privacyChecked, setPrivacyChecked] = useState(false);
    const [contractChecked, setContractChecked] = useState(false);
    // Arranca contando: el primer código sale solo al entrar.
    const [resendIn, setResendIn] = useState(OTP_RESEND_SECONDS);
    // Evita un segundo envío por doble clic antes de que el botón se deshabilite.
    const sending = useRef(false);
    // Spinner del reenvío manual; no usa `isPending`, que en StrictMode puede quedarse en true.
    const [resending, setResending] = useState(false);
    /** Qué pasó con el último intento de envío. `undefined` mientras no haya fallado ninguno. */
    const [sendFailure, setSendFailure] = useState<string | undefined>(undefined);
    /** El silo rebotó «Continuar» por el correo: la sección se marca. */
    const [emailHighlight, setEmailHighlight] = useState(false);
    /** Correo que términos rechazó por ser de otro cliente; la sesión releída ya no lo trae. */
    const [takenEmail, setTakenEmail] = useState<TakenEmail | undefined>(undefined);
    const inputs = useRef<(HTMLInputElement | null)[]>([]);
    const firstOtpSent = useRef(false);

    // El cliente ya aceptó en esta venta y quedó guardado: al retomarla las casillas se pintan
    // marcadas y quedan fuera de juego. Solo refleja lo que ya está — el consentimiento no se
    // re-registra ni se pisa (ver `handleContinue`), y desmarcarlas tampoco lo revocaría.
    const consentsAccepted = session?.consentsAccepted === true;
    const privacyOk = consentsAccepted || privacyChecked;
    const contractOk = consentsAccepted || contractChecked;

    useEffect(() => {
        if (resendIn <= 0) return;
        const t = setTimeout(() => {
            setResendIn((n) => n - 1);
        }, 1000);
        return () => {
            clearTimeout(t);
        };
    }, [resendIn]);

    // Dispara el PRIMER OTP al entrar a la pantalla. `startSession` NO manda OTP (solo crea la sesión):
    // el envío vive exclusivamente en `resend`. Sin esto, el código nunca sale hasta que el vendedor
    // pica "Reenviar" a los 60 s — y "no llega el SMS" aunque todo esté bien. Se hace una sola vez por
    // montaje y solo si el teléfono no está probado: reanudar lo deja sin probar aunque ya se hubiera
    // verificado antes, y ahí el código tiene que salir de nuevo.
    useEffect(() => {
        if (firstOtpSent.current || !session) return;
        if (otpSettled(session)) return;
        firstOtpSent.current = true;
        void resendOtp.mutateAsync().catch((error: unknown) => {
            // Sin envío, la pantalla lo dice; si fue por espera cuenta ese tiempo, si no deja reintentar.
            setSendFailure(sendOtpMessage(error));
            setResendIn(resendWaitSeconds(error) ?? 0);
        });
    }, [session, resendOtp]);

    if (!session) return <Loading size="lg" />;

    const otpDone = otpSettled(session);
    const canContinue = otpDone && emailSettled(session) && privacyOk && contractOk;
    // 502 reintentable del 01d: no se pudo leer si el cliente ya tiene cuenta.
    const accountSignalDown =
        acceptConsents.error instanceof ApiError &&
        acceptConsents.error.code === 'ACCOUNT_SIGNAL_UNAVAILABLE';
    const accountOpening = hasAccountOpeningMark(session);
    const resumeText = session.resumeOtpRequired
        ? resumeReasonText(session.resumeTarget?.reason, accountOpening)
        : undefined;

    // El silo distingue vencido, ya usado, sin intentos y externo caído: cada uno manda al vendedor a
    // hacer algo distinto. Un mensaje único los aplastaba todos en "Código incorrecto".
    const submitCode = async (full: string): Promise<void> => {
        try {
            await verifyOtp.mutateAsync(full);
            toast.success('Código verificado');
        } catch (error) {
            toast.error(verifyOtpMessage(error));
            setCode(emptyOtpCode());
            inputs.current[0]?.focus();
        }
    };

    /** Pide un código nuevo y reporta lo que de verdad respondió el silo. */
    const handleResend = async (): Promise<void> => {
        if (sending.current || resendIn > 0) return;
        sending.current = true;
        setResending(true);
        try {
            await resendOtp.mutateAsync();
            setSendFailure(undefined);
            setResendIn(OTP_RESEND_SECONDS);
            toast.success(otpSentMessage(formatPhone(session.phone)));
        } catch (error) {
            const message = sendOtpMessage(error);
            setSendFailure(message);
            const seconds = resendWaitSeconds(error);
            if (seconds !== undefined) setResendIn(seconds);
            toast.error(message);
        } finally {
            sending.current = false;
            setResending(false);
        }
    };

    // El destino: la pantalla desde la que se guardó (`resumeSlug`), si no el primer hueco que calcula
    // el silo (`resumeTarget`), y en una venta nueva el KYC.
    const handleContinue = async (): Promise<void> => {
        // El consentimiento ya dado no se pisa: reenviarlo repetiría la resolución de identidad y de
        // ruta del cliente sin que nada haya cambiado.
        if (!consentsAccepted) {
            try {
                await acceptConsents.mutateAsync();
            } catch (error) {
                // Sin consentimiento no hay identidad Fiado resuelta: la venta no puede avanzar.
                if (error instanceof ApiError && error.code === 'ACCOUNT_SIGNAL_UNAVAILABLE')
                    return;
                // El silo no ve el correo verificado: se relee la sesión y se marca la sección.
                if (error instanceof ApiError && error.code === 'EMAIL_NOT_VERIFIED') {
                    setEmailHighlight(true);
                    toast.error(EMAIL_NOT_VERIFIED_MESSAGE);
                    void refresh();
                    return;
                }
                // El correo es de otro cliente: se guarda antes de releer, porque en PENDING no viene.
                if (isEmailAlreadyExists(error)) {
                    setTakenEmail({ address: session.email?.address, kind: session.email?.kind });
                    void refresh();
                    return;
                }
                toast.error(
                    wizardErrorMsg(
                        error,
                        'No se pudieron registrar los términos. Intenta de nuevo.',
                    ),
                );
                return;
            }
        }
        await navigate({
            to: '/wizard/s/$sessionId/$step',
            params: { sessionId, step: stepAfterOtp(session) },
        });
    };

    return (
        <WizardCard>
            {accountOpening ? (
                <WizardCardHead
                    eyebrow={ACCOUNT_OPENING_OTP_EYEBROW}
                    title="Verifica el teléfono y el correo"
                    sub={ACCOUNT_OPENING_OTP_SUB}
                />
            ) : (
                <WizardCardHead
                    eyebrow="Inicio"
                    title="Verifica el teléfono y el correo"
                    sub="Ingresa los códigos y acepta los términos."
                />
            )}

            <WizardCardBody>
                {/* Cliente reconocido (gate Fiado D27) — banda informativa con tonos suaves del token. */}
                {session.phoneStatus === 'RECOGNIZED' && session.firstName && (
                    <div className="mb-4 rounded-md border border-info-soft-border bg-info-soft px-3 py-2 text-[11.5px] leading-relaxed text-info">
                        Cliente reconocido: <span className="font-medium">{session.firstName}</span>
                        .
                    </div>
                )}

                {/* Venta retomada: el código es nuevo aunque el cliente ya hubiera pasado uno. */}
                {session.resumeOtpRequired && (
                    <div className="mb-4 rounded-md border border-warning-soft-border bg-warning-soft px-3 py-2 text-[11.5px] leading-relaxed text-warning">
                        {resumedText(Boolean(sendFailure), accountOpening)}
                        {resumeText && <span className="block font-medium">{resumeText}</span>}
                    </div>
                )}

                {/* El envío no salió: decirlo aquí y no en un toast, que se va antes de leerlo. */}
                {sendFailure && !otpDone && (
                    <div className="mb-4 rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-[11.5px] leading-relaxed text-destructive">
                        {sendFailure}
                    </div>
                )}

                {accountSignalDown && (
                    <div className="mb-4 flex flex-wrap items-center justify-between gap-2 rounded-md border border-warning-soft-border bg-warning-soft px-3 py-2 text-[11.5px] leading-relaxed text-warning">
                        <span>No pudimos revisar si ya tiene cuenta. Intenta de nuevo.</span>
                        <Button
                            variant="outline"
                            size="sm"
                            isLoading={acceptConsents.isPending}
                            onClick={() => void handleContinue()}
                        >
                            Reintentar
                        </Button>
                    </div>
                )}

                {/* ── Sub-paso B: código recibido ── */}
                {!otpDone && (
                    <div className="flex flex-col gap-3">
                        <div className="flex flex-wrap items-center justify-between gap-2">
                            <span className="text-[10px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">
                                Código recibido
                            </span>
                            <span className="text-[11.5px] text-muted-foreground">
                                Te mandamos el código al{' '}
                                <span className="font-medium tabular-nums text-foreground">
                                    {formatPhone(session.phone)}
                                </span>
                                . {OTP_LIFETIME_TEXT}
                                {' · '}
                                {/* Regresa a capturar el teléfono SIN salir del wizard (por si se tecleó mal). */}
                                <button
                                    type="button"
                                    onClick={() => void navigate({ to: '/wizard' })}
                                    className="font-medium text-primary underline decoration-dotted underline-offset-2 transition-colors hover:text-primary/80"
                                >
                                    Cambiar teléfono
                                </button>
                            </span>
                        </div>

                        <div className="flex flex-wrap items-center gap-1.5 sm:gap-2">
                            <OtpCodeBoxes
                                code={code}
                                onCodeChange={setCode}
                                onComplete={(full) => void submitCode(full)}
                                inputsRef={inputs}
                            />
                            <Button
                                variant="ghost"
                                size="sm"
                                className="ml-0 sm:ml-2"
                                disabled={resendIn > 0}
                                isLoading={resending}
                                onClick={() => void handleResend()}
                            >
                                {resendIn > 0
                                    ? `Reenviar en ${formatCountdown(resendIn)}`
                                    : 'Reenviar'}
                            </Button>
                        </div>
                    </div>
                )}

                {/* Teléfono ya verificado — reemplaza al bloque de casillas. */}
                {otpDone && (
                    <div className="rounded-md border border-success-soft-border bg-success-soft px-3 py-2 text-[12px] font-medium text-success">
                        {`Teléfono ${formatPhone(session.phone)} verificado.`}
                    </div>
                )}

                {/* ── Correo: mismo peso que el teléfono ── */}
                <EmailVerifyBlock
                    sessionId={sessionId}
                    session={session}
                    highlight={emailHighlight}
                    taken={takenEmail}
                    onEmailVerified={() => {
                        setTakenEmail(undefined);
                    }}
                />

                {/* ── Sub-paso C: aceptar términos ── */}
                <div className="mt-6 flex flex-col gap-3">
                    <div className="h-px bg-border" />
                    <span className="text-[10px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">
                        Aceptar términos
                    </span>

                    <ConsentCheck
                        checked={privacyOk}
                        onChange={setPrivacyChecked}
                        locked={consentsAccepted}
                        title="Aviso de Privacidad"
                        description="Autoriza el tratamiento de datos personales para el proceso comercial y financiero."
                        docType="PRIVACY"
                    />
                    <ConsentCheck
                        checked={contractOk}
                        onChange={setContractChecked}
                        locked={consentsAccepted}
                        title="Contrato de la cuenta PagoConFiado"
                        description="Habilita la apertura automática de la cuenta con la que el cliente operará sus pagos."
                        docType="PCF_CONTRACT"
                    />

                    <p className="text-[11.5px] leading-relaxed text-muted-foreground">
                        {consentHint(consentsAccepted, accountOpening)}
                    </p>
                </div>
            </WizardCardBody>

            <WizardFootNav
                back={
                    <Link
                        to="/sales"
                        className="text-[13px] text-muted-foreground transition-colors hover:text-foreground"
                    >
                        Cancelar
                    </Link>
                }
            >
                <Button
                    disabled={!canContinue}
                    isLoading={acceptConsents.isPending}
                    rightIcon={<ArrowRight className="size-3.5" aria-hidden />}
                    onClick={() => void handleContinue()}
                >
                    Continuar
                </Button>
            </WizardFootNav>
        </WizardCard>
    );
}
